import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input, Select, Segmented, Spin, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useFeedback } from '../../../utils/feedback';
import { formatFinanceGrouped, formatFinancePercent } from '../pendingInvoiceCost';
import { COMPANY_COST_CATEGORIES } from '../../companyCosts/api/companyCostsApi';
import { resolveCompanyBalanceRange } from '../companyBalance/companyBalancePeriod';
import {
  fetchIncomeStatement,
  type IncomeStatementBasis,
  type IncomeStatementCustomerOrder,
  type IncomeStatementResponse,
} from './incomeStatementApi';
import {
  buildCustomerRows,
  buildCustomersTree,
  buildPeriodOptions,
  buildPnlRows,
  buildRevenueTree,
  customerMarginBucket,
  defaultPeriodValue,
  filterCustomerRows,
  formatTaxId,
  monthShortLabel,
  nextCustomerSort,
  parsePeriodValue,
  periodOptionLabel,
  sortCustomerRows,
  type CustomerRow,
  type CustomerSort,
  type CustomerSortKey,
  type PnlRow,
} from './incomeStatement';
import DrillDonut from './DrillDonut';
import ForecastVsActualSection from './ForecastVsActualSection';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function periodRangeLabel(from: string, to: string, language: string): string {
  return from === to ? monthShortLabel(from, language) : `${monthShortLabel(from, language)}–${monthShortLabel(to, language)}`;
}

// WP13: `plannedDate` is the first day of the ZK's forecast month (chosen
// month, else the month of its orderDate), so only the month is shown.
function forecastMonthLabel(value: string | null, language: string): string {
  if (!value) return '—';
  return monthShortLabel(value.slice(0, 7), language);
}

function pnlRowClassName(row: PnlRow): string {
  const classes: string[] = [];
  if (row.kind === 'group') classes.push('is-group');
  if (row.kind === 'child') classes.push('is-child');
  if (row.kind === 'total' || row.kind === 'final') classes.push('is-total');
  if (row.kind === 'final') classes.push('is-final');
  if (row.kind === 'percent') classes.push('is-pct');
  if (row.sign === 'minus') classes.push('is-minus');
  return classes.join(' ');
}

function pnlRowLabel(row: PnlRow, t: TranslateFn): string {
  if ('category' in row.label) {
    return t(`fixedCosts:categories.${row.label.category}`, { defaultValue: row.label.category });
  }
  return t(`reports:profitability.incomeStatement.table.rows.${row.label.key}`);
}

const CUSTOMER_COLUMNS: { key: CustomerSortKey; labelKey: string }[] = [
  { key: 'name', labelKey: 'colCustomer' },
  { key: 'orderCount', labelKey: 'colOrders' },
  { key: 'revenue', labelKey: 'colRevenue' },
  { key: 'orderCosts', labelKey: 'colCosts' },
  { key: 'margin', labelKey: 'colMargin' },
  { key: 'marginPercent', labelKey: 'colMarginPercent' },
  { key: 'share', labelKey: 'colShare' },
];

const IncomeStatementWorkspace: React.FC = () => {
  const { t, i18n } = useTranslation(['reports', 'orders', 'fixedCosts', 'common']);
  const language = i18n.language || 'pl';
  const feedback = useFeedback();
  const navigate = useNavigate();

  const [periodValue, setPeriodValue] = useState<string>(() => defaultPeriodValue(dayjs()));
  const [basis, setBasis] = useState<IncomeStatementBasis>('forecast');
  const [data, setData] = useState<IncomeStatementResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [fixedExpanded, setFixedExpanded] = useState(false);
  const [revenuePath, setRevenuePath] = useState<string[]>([]);
  const [customersPath, setCustomersPath] = useState<string[]>([]);
  const [lastDrill, setLastDrill] = useState<'revenue' | 'customers'>('revenue');
  const [customerSort, setCustomerSort] = useState<CustomerSort>({ key: 'margin', direction: 'desc' });
  const [query, setQuery] = useState('');
  const [openCustomerKey, setOpenCustomerKey] = useState<string | null>(null);
  const [flashCustomerKey, setFlashCustomerKey] = useState<string | null>(null);
  const [outsidePlanOpen, setOutsidePlanOpen] = useState(false);

  const periodGroups = useMemo(() => buildPeriodOptions(dayjs()), []);

  const range = useMemo(() => {
    const parsed = parsePeriodValue(periodValue);
    if (!parsed) return resolveCompanyBalanceRange(dayjs(), 'YEAR');
    return resolveCompanyBalanceRange(dayjs(`${parsed.anchor}-01`), parsed.period);
  }, [periodValue]);

  // Fetch on range change — mirrors the cancelled-flag pattern in
  // CompanyBalanceWorkspace.tsx so a fast period switch cannot let a slow,
  // stale response overwrite a fresher one.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchIncomeStatement(range.from, range.to)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch(() => {
        if (!cancelled) feedback.error(t('reports:profitability.incomeStatement.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  // A stale drill path or an open customer row from a previous period/basis
  // makes no sense once the underlying data changes — reset both.
  useEffect(() => {
    setRevenuePath([]);
    setCustomersPath([]);
    setOpenCustomerKey(null);
  }, [data, basis]);

  // Escape pops the most recently drilled donut (falling back to the other
  // one), ignoring keystrokes aimed at a form control (the period Select,
  // the search input) or already handled elsewhere.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target && /^(input|select|textarea)$/i.test(target.tagName)) return;
      const secondary = lastDrill === 'revenue' ? 'customers' : 'revenue';
      const primaryPath = lastDrill === 'revenue' ? revenuePath : customersPath;
      const secondaryPath = secondary === 'revenue' ? revenuePath : customersPath;
      const targetDrill = primaryPath.length > 0 ? lastDrill : (secondaryPath.length > 0 ? secondary : null);
      if (!targetDrill) return;
      event.preventDefault();
      if (targetDrill === 'revenue') setRevenuePath((path) => path.slice(0, -1));
      else setCustomersPath((path) => path.slice(0, -1));
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lastDrill, revenuePath, customersPath]);

  // Scrolls the just-opened row into view and clears the flash highlight
  // after 1.7s — declarative cleanup instead of a stray setTimeout in the
  // click handler, so a rapid second donut click cannot leak a timer.
  useEffect(() => {
    if (!flashCustomerKey) return undefined;
    const row = Array.from(document.querySelectorAll<HTMLElement>('[data-customer-key]'))
      .find((el) => el.getAttribute('data-customer-key') === flashCustomerKey);
    // Optional call, not just optional access — jsdom (unit tests) does not
    // implement scrollIntoView at all, so `row?.scrollIntoView(...)` alone
    // would still throw when a row is found.
    row?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(() => setFlashCustomerKey(null), 1700);
    return () => window.clearTimeout(timer);
  }, [flashCustomerKey]);

  const periodSelectOptions = useMemo(() => periodGroups.map((group) => ({
    label: t(`reports:companyBalance.periods.${group.key}`),
    options: group.options.map((option) => ({
      value: option.value,
      label: periodOptionLabel(option, t, language),
    })),
  })), [periodGroups, t, language]);

  const line = data ? data.totals[basis] : null;

  const pnlRows = useMemo(() => (
    data ? buildPnlRows(data.months, data.totals[basis], basis, COMPANY_COST_CATEGORIES) : []
  ), [data, basis]);

  const revenueTreeLabels = useMemo(() => ({
    revenueRoot: t('reports:profitability.incomeStatement.structure.revenueRoot'),
    orderCosts: t('reports:profitability.incomeStatement.table.rows.orderCosts'),
    margin: t('reports:profitability.incomeStatement.table.rows.margin'),
    operatingFixedCosts: t('reports:profitability.incomeStatement.table.rows.operatingFixedCosts'),
    otherCategories: (count: number) => `${t('reports:profitability.incomeStatement.structure.otherCategories')} ${t('reports:profitability.incomeStatement.structure.otherCount', { count })}`,
    ebitda: t('reports:profitability.incomeStatement.table.rows.ebitda'),
    depreciation: t('reports:profitability.incomeStatement.table.rows.depreciation'),
    financialCosts: t('reports:profitability.incomeStatement.table.rows.financialCosts'),
    incomeTax: t('reports:profitability.incomeStatement.table.rows.incomeTax'),
    profit: t(`reports:profitability.incomeStatement.table.rows.${basis === 'actual' ? 'profitActual' : 'profitForecast'}`),
  }), [t, basis]);

  const categoryLabel = useMemo(() => (code: string) => t(`fixedCosts:categories.${code}`, { defaultValue: code }), [t]);

  const revenueTree = useMemo(() => (
    line ? buildRevenueTree(line, revenueTreeLabels, categoryLabel) : null
  ), [line, revenueTreeLabels, categoryLabel]);

  const customerRows = useMemo(() => (
    data ? buildCustomerRows(data.customers, basis) : []
  ), [data, basis]);

  const customersTreeLabels = useMemo(() => ({
    root: t('reports:profitability.incomeStatement.structure.customersRoot'),
    otherCustomers: (count: number) => `${t('reports:profitability.incomeStatement.structure.otherCustomers')} ${t('reports:profitability.incomeStatement.structure.otherCount', { count })}`,
  }), [t]);

  const customersTree = useMemo(() => buildCustomersTree(customerRows, customersTreeLabels), [customerRows, customersTreeLabels]);

  const visibleRows = useMemo(() => (
    sortCustomerRows(filterCustomerRows(customerRows, query, language), customerSort, language)
  ), [customerRows, query, customerSort, language]);

  const maxAbsShare = useMemo(() => (
    Math.max(1, ...customerRows.map((row) => Math.abs(row.share ?? 0)))
  ), [customerRows]);

  const totalOrderCount = useMemo(() => customerRows.reduce((sum, row) => sum + row.orderCount, 0), [customerRows]);

  const customersMarginSum = useMemo(() => customerRows.reduce<number | null>(
    (sum, row) => (sum == null || row.margin == null ? null : sum + row.margin),
    0,
  ), [customerRows]);

  function toggleCustomerRow(key: string) {
    setOpenCustomerKey((current) => (current === key ? null : key));
  }

  function handleDonutCustomerSelect(customerKey: string) {
    setOpenCustomerKey(customerKey);
    setFlashCustomerKey(customerKey);
  }

  function identityMeta(row: CustomerRow): string {
    const parts: string[] = [];
    const formattedTaxId = formatTaxId(row.taxId);
    if (formattedTaxId) parts.push(t('reports:profitability.incomeStatement.customers.taxId', { taxId: formattedTaxId }));
    if (row.address) parts.push(row.address);
    if (parts.length === 0) {
      return row.id ? '' : t('reports:profitability.incomeStatement.customers.noCustomer');
    }
    return parts.join(' · ');
  }

  function sortableHeader(key: CustomerSortKey, label: string) {
    const active = customerSort.key === key;
    const ariaSort = active ? (customerSort.direction === 'asc' ? 'ascending' : 'descending') : 'none';
    return (
      <th key={key} aria-sort={ariaSort} className={active ? `is-sorted ${customerSort.direction}` : ''}>
        <button
          type="button"
          onClick={() => setCustomerSort((current) => nextCustomerSort(current, key))}
          aria-label={t('reports:profitability.incomeStatement.customers.sortBy', { column: label })}
        >
          {label}
        </button>
      </th>
    );
  }

  const periodLabel = periodRangeLabel(range.from, range.to, language);
  const scopeLabel = t(basis === 'actual' ? 'reports:profitability.incomeStatement.summary.scopeActual' : 'reports:profitability.incomeStatement.summary.scopeForecast', { period: periodLabel });
  const description = t(basis === 'actual' ? 'reports:profitability.incomeStatement.descriptionActual' : 'reports:profitability.incomeStatement.descriptionForecast');
  const profitFinalClass = line?.profit == null ? '' : (line.profit < 0 ? 'is-negative' : 'is-positive');

  return (
    <div className="profitability-income">
      <header className="profitability-income__header">
        <div className="profitability-income__header-copy">
          <span className="profitability-income__eyebrow">{t('reports:profitability.incomeStatement.eyebrow')}</span>
          <h2 className="profitability-income__title">{t('reports:profitability.incomeStatement.title')}</h2>
          <p className="profitability-income__description">{description}</p>
        </div>
        <div className="profitability-income__scope">
          <Select
            value={periodValue}
            onChange={setPeriodValue}
            options={periodSelectOptions}
            popupMatchSelectWidth={false}
            disabled={loading}
            aria-label={t('reports:profitability.incomeStatement.period')}
          />
          <Segmented<IncomeStatementBasis>
            value={basis}
            onChange={setBasis}
            options={[
              { value: 'forecast', label: t('reports:profitability.incomeStatement.basis.forecast') },
              { value: 'actual', label: t('reports:profitability.incomeStatement.basis.actual') },
            ]}
            disabled={loading}
            aria-label={t('reports:profitability.incomeStatement.basis.label')}
          />
        </div>
      </header>

      {!data ? (
        <div className="profitability-income-loading"><Spin size="large" /></div>
      ) : (
        <>
          <div className="profitability-income__summary-head">
            <span className="profitability-income__eyebrow">{t('reports:profitability.incomeStatement.summary.kicker')}</span>
            <span>{scopeLabel}</span>
          </div>
          <section className="profitability-income__summary" aria-label={t('reports:profitability.incomeStatement.summary.kicker')}>
            <div className="profitability-income__stage">
              <span className="profitability-income__stage-label">
                {t(basis === 'actual' ? 'reports:profitability.incomeStatement.summary.revenueActual' : 'reports:profitability.incomeStatement.summary.revenueForecast')}
              </span>
              <span className="profitability-income__stage-value">{formatFinanceGrouped(line?.revenue ?? null)}<small>zł</small></span>
              <span className="profitability-income__stage-sub">
                <span>{t('reports:profitability.incomeStatement.summary.revenueHint')}</span>
              </span>
            </div>
            <div className="profitability-income__stage">
              <span className="profitability-income__stage-label">{t('reports:profitability.incomeStatement.summary.margin')}</span>
              <span className="profitability-income__stage-value">{formatFinanceGrouped(line?.margin ?? null)}<small>zł</small></span>
              <span className="profitability-income__stage-sub">
                <span className="m">{t('reports:profitability.incomeStatement.summary.minusOrderCosts')} <b>{formatFinanceGrouped(line?.orderCosts ?? null)}</b></span>
                <span>{t('reports:profitability.incomeStatement.summary.shareOfRevenue', { percent: formatFinancePercent(line?.marginPercent ?? null, language) })}</span>
              </span>
            </div>
            <div className="profitability-income__stage">
              <span className="profitability-income__stage-label">{t('reports:profitability.incomeStatement.summary.ebitda')}</span>
              <span className="profitability-income__stage-value">{formatFinanceGrouped(line?.ebitda ?? null)}<small>zł</small></span>
              <span className="profitability-income__stage-sub">
                <span className="m">{t('reports:profitability.incomeStatement.summary.minusOperatingFixedCosts')} <b>{formatFinanceGrouped(line?.operatingFixedCosts.total ?? null)}</b></span>
                <span>{t('reports:profitability.incomeStatement.summary.shareOfRevenue', { percent: formatFinancePercent(line?.ebitdaPercent ?? null, language) })}</span>
              </span>
            </div>
            <div className={`profitability-income__stage profitability-income__stage--final ${profitFinalClass}`}>
              <span className="profitability-income__stage-label">
                {t(basis === 'actual' ? 'reports:profitability.incomeStatement.summary.profitActual' : 'reports:profitability.incomeStatement.summary.profitForecast')}
              </span>
              <span className="profitability-income__stage-value">{formatFinanceGrouped(line?.profit ?? null)}<small>zł</small></span>
              <span className="profitability-income__stage-sub">
                <span className="m">
                  {t('reports:profitability.incomeStatement.summary.minusBelowEbitda')}{' '}
                  <b>{formatFinanceGrouped(line && line.incomeTax != null ? line.depreciation + line.financialCosts + line.incomeTax : null)}</b>
                </span>
                <span>{t('reports:profitability.incomeStatement.summary.shareOfRevenue', { percent: formatFinancePercent(line?.profitPercent ?? null, language) })}</span>
              </span>
            </div>
          </section>

          {basis === 'forecast' && data.awaitingSettlementSummary.orderCount > 0 && (
            <div className="profitability-income__awaiting-settlement" data-testid="income-statement-awaiting-settlement">
              {t('reports:profitability.incomeStatement.summary.awaitingSettlement', {
                count: data.awaitingSettlementSummary.orderCount,
                amount: `${formatFinanceGrouped(data.awaitingSettlementSummary.revenue)} zł`,
              })}
            </div>
          )}

          {data.forecastVsActual && (
            <ForecastVsActualSection
              block={data.forecastVsActual}
              purchases={data.purchases || []}
              periodLabel={periodLabel}
              language={language}
              t={t}
            />
          )}

          <section className="profitability-income__section">
            <div className="profitability-income__section-head">
              <div>
                <span className="profitability-income__eyebrow">{t('reports:profitability.incomeStatement.table.kicker')}</span>
                <h3>{t('reports:profitability.incomeStatement.table.title')}</h3>
                <p>{t('reports:profitability.incomeStatement.table.hint')}</p>
              </div>
            </div>
            <div className="profitability-income__panel">
              <div className="profitability-income-pnl-wrap">
                <table className="profitability-income-pnl" data-testid="income-statement-pnl">
                  <thead>
                    <tr>
                      <th>{t('reports:profitability.incomeStatement.table.colItem')}</th>
                      {data.months.map((month) => (
                        <th key={month.month} className={month.isCurrent ? 'is-current' : ''}>
                          {monthShortLabel(month.month, language)}
                          {month.isCurrent && <span className="sub">{t('reports:profitability.incomeStatement.periodCurrentMonth')}</span>}
                        </th>
                      ))}
                      <th className="col-total">
                        {periodLabel}
                        <span className="sub">{t('reports:profitability.incomeStatement.table.colTotal')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pnlRows.filter((row) => row.kind !== 'child' || fixedExpanded).map((row) => (
                      <tr key={row.key} className={pnlRowClassName(row)}>
                        <td>
                          {row.kind === 'group' ? (
                            <button
                              type="button"
                              className="pnl-toggle"
                              aria-expanded={fixedExpanded}
                              onClick={() => setFixedExpanded((expanded) => !expanded)}
                            >
                              <span className="pnl-toggle__chev" />
                              {pnlRowLabel(row, t)}
                            </button>
                          ) : pnlRowLabel(row, t)}
                          {row.hintKey && (
                            <span className="pnl-hint" title={t(`reports:profitability.incomeStatement.table.rows.${row.hintKey}`)}>
                              {t(`reports:profitability.incomeStatement.table.rows.${row.hintKey}`)}
                            </span>
                          )}
                        </td>
                        {row.values.map((value, index) => {
                          const negative = row.signed && value != null && value < 0;
                          return (
                            <td key={`${row.key}-${index}`} className={negative ? 'is-negative' : ''}>
                              {row.isPercent ? formatFinancePercent(value, language) : formatFinanceGrouped(value)}
                            </td>
                          );
                        })}
                        <td className={`col-total ${row.signed && row.total != null && row.total < 0 ? 'is-negative' : ''}`}>
                          {row.isPercent ? formatFinancePercent(row.total, language) : formatFinanceGrouped(row.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!!data.purchases?.filter(row => basis === 'forecast' || row.actual).length && <details style={{ marginTop: 16 }}>
                  <summary>{t('reports:profitability.incomeStatement.purchaseSources.title')}</summary>
                  <p>{t('reports:profitability.incomeStatement.purchaseSources.hint')}</p>
                  <table className="profitability-income-firms" data-testid="income-statement-purchases">
                    <thead><tr>{['order','month','contract','invoice','net','extra'].map(key => <th key={key}>{t(`reports:profitability.incomeStatement.purchaseSources.${key}`)}</th>)}</tr></thead>
                    <tbody>{data.purchases.filter(row => basis === 'forecast' || row.actual).map(row => <tr key={`${row.kind}-${row.supplierOrderId}-${row.invoiceId || 'plan'}`}>
                      <td><Link to={`/app/supplier-orders/${row.supplierOrderId}`}>{row.orderNumber}</Link><br />{row.supplierName}</td>
                      <td>{row.month}</td>
                      <td>{formatFinanceGrouped(row.contractNet, row.contractCurrency)}{row.contractCurrency !== 'PLN' && <><br />{t('reports:profitability.incomeStatement.purchaseSources.rate')}: {row.contractRate ?? '—'}<br />{formatFinanceGrouped(row.contractNetPLN)}</>}</td>
                      <td>{row.invoiceId ? <><Link to={`/app/purchase-invoices/${row.invoiceId}`}>{row.documentNumber}</Link>{(row.kind === 'CORRECTION' || row.kind === 'WITHDRAWAL') && <> · {t(`reports:profitability.incomeStatement.purchaseSources.kinds.${row.kind}`)}</>}<br />{formatFinanceGrouped(row.invoiceNet, row.invoiceCurrency ?? 'PLN')}{row.invoiceCurrency !== 'PLN' && <><br />{t('reports:profitability.incomeStatement.purchaseSources.rate')}: {row.invoiceRate ?? '—'}</>}</> : t('reports:profitability.incomeStatement.purchaseSources.plan')}</td>
                      <td>{formatFinanceGrouped(row.netPLN)}</td><td>{formatFinanceGrouped(row.extraCostPLN)}</td>
                    </tr>)}</tbody>
                  </table>
                </details>}
              </div>
            </div>
          </section>

          <section className="profitability-income__section">
            <div className="profitability-income__section-head">
              <div>
                <span className="profitability-income__eyebrow">{t('reports:profitability.incomeStatement.structure.kicker')}</span>
                <h3>{t('reports:profitability.incomeStatement.structure.title')}</h3>
                <p>{t('reports:profitability.incomeStatement.structure.hint')}</p>
              </div>
            </div>
            <div className="profitability-income__panel profitability-income-struct">
              <div>
                <h4>{t('reports:profitability.incomeStatement.structure.revenueTitle')}</h4>
                <DrillDonut
                  root={revenueTree}
                  path={revenuePath}
                  onPathChange={(next) => {
                    setRevenuePath(next);
                    setLastDrill('revenue');
                  }}
                  centerLabelRoot={t('reports:profitability.incomeStatement.structure.centerRevenue')}
                  centerLabelDrilled={t('reports:profitability.incomeStatement.structure.centerTotal')}
                  lossLabel={t('reports:profitability.incomeStatement.structure.centerLoss')}
                  rootTip={t('reports:profitability.incomeStatement.structure.rootTip')}
                  backLabel={t('reports:profitability.incomeStatement.structure.back')}
                  backHintLabel={t('reports:profitability.incomeStatement.structure.backHint')}
                  emptyMessage={t('reports:profitability.incomeStatement.structure.pendingCost')}
                  ariaLabel={t('reports:profitability.incomeStatement.structure.revenueTitle')}
                  language={language}
                  testId="income-statement-revenue-donut"
                />
              </div>
              <div>
                <h4>{t('reports:profitability.incomeStatement.structure.customersTitle')}</h4>
                <DrillDonut
                  root={customersTree}
                  path={customersPath}
                  onPathChange={(next) => {
                    setCustomersPath(next);
                    setLastDrill('customers');
                  }}
                  onLeafSelect={(node) => node.customerKey && handleDonutCustomerSelect(node.customerKey)}
                  centerLabelRoot={t('reports:profitability.incomeStatement.structure.centerMargin')}
                  centerLabelDrilled={t('reports:profitability.incomeStatement.structure.centerTotal')}
                  lossLabel={t('reports:profitability.incomeStatement.structure.centerLoss')}
                  rootTip={t('reports:profitability.incomeStatement.structure.customersTip', { count: customerRows.length })}
                  backLabel={t('reports:profitability.incomeStatement.structure.back')}
                  backHintLabel={t('reports:profitability.incomeStatement.structure.backHint')}
                  emptyMessage={t('reports:profitability.incomeStatement.structure.noCustomers')}
                  ariaLabel={t('reports:profitability.incomeStatement.structure.customersTitle')}
                  language={language}
                  testId="income-statement-customers-donut"
                />
              </div>
            </div>
          </section>

          <section className="profitability-income__section">
            <div className="profitability-income__section-head">
              <div>
                <span className="profitability-income__eyebrow">{t('reports:profitability.incomeStatement.customers.kicker')}</span>
                <h3>
                  {t('reports:profitability.incomeStatement.customers.title', {
                    customers: customerRows.length,
                    orders: totalOrderCount,
                    period: periodLabel,
                  })}
                </h3>
                <p>{t('reports:profitability.incomeStatement.customers.hint')}</p>
              </div>
              <div className="profitability-income-firms-tools">
                <Input
                  prefix={<SearchOutlined />}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('reports:profitability.incomeStatement.customers.searchPlaceholder')}
                  aria-label={t('reports:profitability.incomeStatement.customers.searchAria')}
                  allowClear
                />
              </div>
            </div>
            <div className="profitability-income__panel">
              <div className="profitability-income-firms-wrap">
                <table className="profitability-income-firms" data-testid="income-statement-customers">
                  <thead>
                    <tr>
                      {CUSTOMER_COLUMNS.map((column) => sortableHeader(
                        column.key,
                        t(`reports:profitability.incomeStatement.customers.${column.labelKey}`),
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length === 0 ? (
                      <tr>
                        <td colSpan={CUSTOMER_COLUMNS.length} className="profitability-income-empty">
                          {query
                            ? t('reports:profitability.incomeStatement.customers.empty', { query })
                            : t('reports:profitability.incomeStatement.customers.noData')}
                        </td>
                      </tr>
                    ) : visibleRows.map((row, index) => {
                      const bucket = customerMarginBucket(row);
                      const isOpen = openCustomerKey === row.key;
                      const shareWidth = row.share == null ? 0 : Math.min(100, Math.max(2, (Math.abs(row.share) / maxAbsShare) * 100));
                      const meta = identityMeta(row);
                      return (
                        <React.Fragment key={row.key}>
                          <tr
                            className={`row ${isOpen ? 'is-open' : ''} ${flashCustomerKey === row.key ? 'is-flash' : ''}`}
                            data-customer-key={row.key}
                            aria-expanded={isOpen}
                            tabIndex={0}
                            onClick={() => toggleCustomerRow(row.key)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                toggleCustomerRow(row.key);
                              }
                            }}
                          >
                            <td>
                              <div className="profitability-income-identity">
                                <span className="profitability-income-identity__rank">{index + 1}</span>
                                <div>
                                  {row.id ? (
                                    <button
                                      type="button"
                                      className="profitability-income-identity__name"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        navigate(`/app/customers/${row.id}`);
                                      }}
                                    >
                                      {row.name}
                                    </button>
                                  ) : (
                                    <span className="profitability-income-identity__name">{row.name}</span>
                                  )}
                                  {meta && <div className="profitability-income-identity__meta">{meta}</div>}
                                </div>
                              </div>
                            </td>
                            <td>{row.orderCount}</td>
                            <td>{formatFinanceGrouped(row.revenue)}</td>
                            <td>{formatFinanceGrouped(row.orderCosts)}</td>
                            <td className={row.margin != null && row.margin < 0 ? 'is-negative' : ''}>{formatFinanceGrouped(row.margin)}</td>
                            <td>
                              <span className={`profitability-income-chip profitability-income-chip--${bucket}`}>
                                {t(`reports:profitability.incomeStatement.customers.chips.${bucket}`)} · {formatFinancePercent(row.marginPercent, language)}
                              </span>
                            </td>
                            <td>
                              <span className={`profitability-income-share ${row.share != null && row.share < 0 ? 'is-neg' : ''}`}>
                                <span className="bar"><i style={{ width: `${shareWidth}%` }} /></span>
                                <span>{formatFinancePercent(row.share, language)}</span>
                              </span>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="detail">
                              <td colSpan={CUSTOMER_COLUMNS.length}>
                                <ul className="profitability-income-orders">
                                  <li className="h">
                                    <span>{t('reports:profitability.incomeStatement.customers.orderCols.order')}</span>
                                    <span>{t('reports:profitability.incomeStatement.customers.orderCols.status')}</span>
                                    <span>{t('reports:profitability.incomeStatement.customers.orderCols.date')}</span>
                                    <span>{t('reports:profitability.incomeStatement.customers.orderCols.revenue')}</span>
                                    <span>{t('reports:profitability.incomeStatement.customers.orderCols.margin')}</span>
                                  </li>
                                  {row.orders.map((order: IncomeStatementCustomerOrder) => {
                                    const basisValues = order[basis];
                                    return (
                                      <li key={order.id}>
                                        <Link to={`/app/orders/${order.id}`}>
                                          {order.displayOrderNumber}
                                          {order.awaitingSettlement && (
                                            <Tag color="gold" style={{ marginLeft: 8 }}>
                                              {t('reports:profitability.incomeStatement.customers.awaitingSettlementTag')}
                                            </Tag>
                                          )}
                                        </Link>
                                        <span>{t(`orders:statusOptions.${order.status}`, { defaultValue: order.status })}</span>
                                        <span>{forecastMonthLabel(order.plannedDate, language)}</span>
                                        <span>{basisValues ? formatFinanceGrouped(basisValues.revenue) : '—'}</span>
                                        <span className={basisValues?.margin != null && basisValues.margin < 0 ? 'is-negative' : ''}>
                                          {basisValues ? formatFinanceGrouped(basisValues.margin) : '—'}
                                        </span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="profitability-income-firms-foot">
                <span>
                  {t('reports:profitability.incomeStatement.customers.footer', {
                    shown: visibleRows.length,
                    total: customerRows.length,
                    margin: formatFinanceGrouped(customersMarginSum),
                  })}
                </span>
              </div>
              {data.outsidePlan.orderCount > 0 && (
                <div className="profitability-income-outside-plan" data-testid="income-statement-outside-plan">
                  <span>
                    {t('reports:profitability.incomeStatement.outsidePlan.summary', {
                      count: data.outsidePlan.orderCount,
                      amount: `${formatFinanceGrouped(data.outsidePlan.revenue)} zł`,
                    })}
                  </span>
                  <button type="button" onClick={() => setOutsidePlanOpen((open) => !open)}>
                    {t(outsidePlanOpen
                      ? 'reports:profitability.incomeStatement.outsidePlan.toggleHide'
                      : 'reports:profitability.incomeStatement.outsidePlan.toggleShow')}
                  </button>
                  <Link to="/app/profitability?tab=analysis">
                    {t('reports:profitability.incomeStatement.outsidePlan.link')}
                  </Link>
                  {outsidePlanOpen && (
                    <table className="profitability-income-outside-plan__table">
                      <thead>
                        <tr>
                          <th>{t('reports:profitability.incomeStatement.outsidePlan.colOrder')}</th>
                          <th>{t('reports:profitability.incomeStatement.outsidePlan.colCustomer')}</th>
                          <th>{t('reports:profitability.incomeStatement.outsidePlan.colDate')}</th>
                          <th>{t('reports:profitability.incomeStatement.outsidePlan.colRevenue')}</th>
                          <th>{t('reports:profitability.incomeStatement.outsidePlan.colReasons')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.outsidePlan.orders.map((order) => (
                          <tr key={order.id}>
                            <td><Link to={`/app/orders/${order.id}`}>{order.displayOrderNumber}</Link></td>
                            <td>{order.customerName}</td>
                            <td>{forecastMonthLabel(order.plannedDate, language)}</td>
                            <td>{formatFinanceGrouped(order.revenue)}</td>
                            <td>
                              {order.reviewReasons.map((reason) => (
                                <Tag key={reason}>
                                  {t(`reports:profitability.incomeStatement.outsidePlan.reasons.${reason}`, { defaultValue: reason })}
                                </Tag>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default IncomeStatementWorkspace;
